import {createSelector} from 'reselect';

import Editor from 'Editor';
import {UnknownNoteLinkedGroup} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {getTablePlugin} from 'notebook/notes/plugins/getTablePlugin';
import {getAllTableNoteParts, getTableNoteParts} from 'notebook/notes/util/getTableNoteParts';
import {EvaluatedCell, EvaluatedTable, FullTableMap} from 'notebook/tables/excess/Evaluator';
import {Table} from 'notebook/tables/TableConstants';
import {Note, NotePart} from 'notebook/util/NoteUtilConstants';

export function getTableFromNotePart(notePart: NotePart): {table: Table} {
  const plugin = getTablePlugin(notePart);
  return plugin.getContent.call({} as Editor, notePart);
}

/**
 * Resolves a table note part's name without materializing the entire table. For structured tables,
 * `getContent` runs the adapter's `toTable(...)` conversion, which is far more work than a name lookup
 * needs — selectors hit this on hot paths. Prefer the plugin's `getSpreadsheetTableName`, falling back
 * to `getContent` only if the plugin does not implement it.
 */
export function getTableNameFromNotePart(notePart: NotePart): string {
  const plugin = getTablePlugin(notePart);
  if (plugin.getSpreadsheetTableName != null) {
    return plugin.getSpreadsheetTableName.call({} as Editor, notePart);
  }
  return getTableFromNotePart(notePart).table.name;
}

/**
 * Describes where a table note part lives within a note. Tables either sit directly
 * on the note (`containedIndex == null`) or are nested inside a note linked group
 * (e.g. mixture prep, stoichiometry) at `note.noteParts[topIndex].item.containedNoteParts[containedIndex]`.
 */
export interface TableNotePartLocation {
  notePart: NotePart;
  topIndex: number;
  containedIndex: number | null;
}

export function getTableNotePartLocations(note: Note): Array<TableNotePartLocation> {
  const locations: Array<TableNotePartLocation> = [];
  note.noteParts.forEach((part, topIndex) => {
    for (const tablePart of getTableNoteParts(part)) {
      if (tablePart === part) {
        locations.push({notePart: tablePart, topIndex, containedIndex: null});
      } else {
        const group = part.item as UnknownNoteLinkedGroup;
        locations.push({
          notePart: tablePart,
          topIndex,
          containedIndex: group.containedNoteParts.indexOf(tablePart),
        });
      }
    }
  });
  return locations;
}

export function findTableNotePartByName(note: Note, tableName: string): NotePart | null {
  // Search flattened table note parts so tables nested inside note linked groups
  // (e.g. mixture prep, stoichiometry) are resolvable by name as well.
  return (
    getAllTableNoteParts(note.noteParts).find((part) => getTableNameFromNotePart(part) === tableName) ?? null
  );
}

export function getTableMapFromNote(note: Note): FullTableMap {
  const tableNoteParts = getAllTableNoteParts(note.noteParts);
  return tableNoteParts.reduce((acc, notePart) => {
    const {table} = getTableFromNotePart(notePart);
    acc[table.name] = table;
    return acc;
  }, {} as FullTableMap);
}

// TODO(BNCH-126510): This is just the start of building a hydrated table selector
// This does not compute errors yet, it just uses the plugin to generate the table object.
export function createHydratedTableSelector(tableName: string): (note: Note) => Table | null {
  return createSelector([(note: Note) => findTableNotePartByName(note, tableName)], (notePart) => {
    if (!notePart) {
      return null;
    }
    const plugin = getTablePlugin(notePart);
    return (plugin.getContent.call({} as Editor, notePart) as {table: Table}).table;
  });
}

export function getEvaluatedTable(tableName: string): (note: Note) => EvaluatedTable | null {
  return (note: Note) => note.noteRuntimeSnapshot?.evaluationSnapshot?.[tableName] ?? null;
}

export function getEvaluatedRow(
  tableName: string,
  rowIndex: number
): (note: Note) => Array<EvaluatedCell> | null {
  return (note: Note) => getEvaluatedTable(tableName)(note)?.[rowIndex] ?? null;
}

export function getEvaluatedCell(
  tableName: string,
  rowIndex: number,
  columnIndex: number
): (note: Note) => EvaluatedCell | null {
  return (note: Note) => getEvaluatedRow(tableName, rowIndex)(note)?.[columnIndex] ?? null;
}
